import React, { useContext, useEffect, useState, useCallback } from 'react';
import { Alert, Box, useTheme, useMediaQuery } from '@mui/material';
import { useMutation, useQuery } from '@apollo/client';
import { Formik, FormikProps, FormikErrors } from 'formik';
import { useNavigate, useParams } from 'react-router-dom';
import DataContext from '../../context/DataProvider/DataContext';
import AgentValid from '../../types/validSchemas/Agent.valid';
import InitAgentInput from '../../types/initValues/AgentInput';
import { AgentInputType } from '../../types/AgentInputType.types';
import SexInit from '../../types/initValues/Sex.init';
import SmallBusinessInit from '../../types/initValues/SmallBusiness.init';
import Agentv2 from '../../types/Agentv2.types';
import updateAgentQuery from '../../Apollo/mutations/updateAgent';
import { AgentStatus } from '../../types/Agent.types';
import createAgentQuery from '../../Apollo/mutations/createAgent';
import getAgentById from '../../Apollo/queries/getAgentById';
import { useLoadingContext } from '../../context/LoadingProvider/LoadingContext';
import PRMBackendAgentInput from '../../types/PRMBackendAgentInput.types';
import formatAgentinputForQuery from '../../services/formatAgentInput';
import AgentInit from '../../types/initValues/Agent.init';
import handleClearStorage from './services/handleClearStorage';
import handleFormChange, { LocalStorageKeys } from './services/handleFormChange';
import AlertSomethingWentWrong from '../../components/Alert';
import AgentSkeleton from './components/AgentSkeleton';
import AgentHeader from './components/AgentHeader';
import AgentSidebar from './components/AgentSidebar';
import PersonalPanel from './components/panels/PersonalPanel';
import ContractPanel from './components/panels/ContractPanel';
import FinancePanel from './components/panels/FinancePanel';
import SystemsPanel from './components/panels/SystemsPanel';
import CostsPanel from './components/panels/CostsPanel';
import SystemAccessPanel from './components/panels/SystemAccessPanel';
import DocumentsPanel from './components/panels/DocumentsPanel';
import NotesPanel from './components/panels/NotesPanel';
import DismissPanel from './components/panels/DismissPanel';
import { PanelId, FIELD_TO_PANEL, getErrorCountsByPanel } from './panelConstants';

export default function AgentEditPage() {
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const { agents, status, rank, level, brachOffices, contractType, country, loaderFun, reloadData } =
    useContext(DataContext);
  const { number: AgentID } = useParams<{ number?: string }>();
  const [createAgent, { loading: loadingCreateNewAgent, error: errorCreateAgent }] = useMutation(createAgentQuery);
  const [updateAgent, { loading: laodingUpdateAgent, error: errorUpdateAgent }] = useMutation(updateAgentQuery);
  const [activePanel, setActivePanel] = useState<PanelId>('personal');
  const { setLoading } = useLoadingContext();
  const navigate = useNavigate();
  const {
    data: agentResponse,
    refetch,
    loading: loadingAgentData,
    error: errorGetAgentById,
  } = useQuery(getAgentById, {
    variables: { agent_id: AgentID ? parseInt(AgentID, 10) : 0 },
    skip: !AgentID,
  });
  setLoading(loadingAgentData || loadingCreateNewAgent || laodingUpdateAgent);

  useEffect(() => {
    loaderFun.loadStatus();
    loaderFun.loadRank();
    loaderFun.loadLevel();
    loaderFun.loadBranchOffice();
    loaderFun.loadContractType();
    loaderFun.loadCountry();
  }, []);

  function agentInputWithDefaultValues(): AgentInputType {
    let withDefaultValues = { ...InitAgentInput };

    status.filter((statusAgent) => {
      if (statusAgent.is_default) {
        withDefaultValues = { ...withDefaultValues, status_id: +statusAgent.id };
      }
      return null;
    });
    SexInit.filter((sexAgent) => {
      if (sexAgent.is_default) {
        withDefaultValues = { ...withDefaultValues, is_male: +sexAgent.id };
      }
      return null;
    });
    rank.filter((rankItemAgent) => {
      if (rankItemAgent.is_default) {
        withDefaultValues = { ...withDefaultValues, rank_id: +rankItemAgent.id };
      }
      return null;
    });
    level.filter((levelItemAgent) => {
      if (levelItemAgent.is_default) {
        withDefaultValues = { ...withDefaultValues, level_id: +levelItemAgent.id };
      }
      return null;
    });
    brachOffices.filter((officeItemAgent) => {
      if (officeItemAgent.is_default) {
        withDefaultValues = {
          ...withDefaultValues,
          branch_office_id: +officeItemAgent.id,
          email_corp: `.@${officeItemAgent.email_postfix}`,
        };
      }
      return null;
    });
    contractType.filter((contractItemAgent) => {
      if (contractItemAgent.is_default) {
        withDefaultValues = { ...withDefaultValues, contract_type_id: +contractItemAgent.id };
      }
      return null;
    });
    country.filter((countryItemAgent) => {
      if (countryItemAgent.is_default) {
        withDefaultValues = { ...withDefaultValues, country_id: +countryItemAgent.id };
      }
      return null;
    });
    SmallBusinessInit.filter((businessItemAgent) => {
      if (businessItemAgent.is_default) {
        withDefaultValues = { ...withDefaultValues, is_small_business: +businessItemAgent.id };
      }
      return null;
    });

    return withDefaultValues;
  }

  const defaultAgentValue = agentInputWithDefaultValues();

  function AgentFromLocalStorage() {
    const formData = localStorage.getItem(LocalStorageKeys.formDataCreateAgent);
    if (formData) {
      return JSON.parse(formData);
    }
    return null;
  }

  const isStatus = status.length > 0;
  const isSex = SexInit.length > 0;
  const isRank = rank.length > 0;
  const isLevel = level.length > 0;
  const isBranchOffice = brachOffices.length > 0;
  const isContractType = contractType.length > 0;
  const isCountry = country.length > 0;
  const isSmallBusiness = SmallBusinessInit.length > 0;

  const handleSaveWithNavigation = useCallback(
    async (
      validateForm: () => Promise<FormikErrors<Agentv2 | AgentInputType>>,
      setTouched: (touched: Record<string, boolean>) => void,
      handleSubmit: () => void,
    ) => {
      const validationErrors = await validateForm();
      const errorKeys = Object.keys(validationErrors);

      if (errorKeys.length > 0) {
        const touchObj: Record<string, boolean> = {};
        errorKeys.forEach((key) => {
          touchObj[key] = true;
        });
        setTouched(touchObj);

        const firstErrorField = errorKeys[0];
        const targetPanel = FIELD_TO_PANEL[firstErrorField];
        if (targetPanel) {
          setActivePanel(targetPanel);
          setTimeout(() => {
            const element = document.querySelector(`[name="${firstErrorField}"]`);
            if (element) {
              (element as HTMLElement).focus();
              element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }, 150);
        }
        return;
      }
      handleSubmit();
    },
    [],
  );

  if (errorGetAgentById || errorCreateAgent || errorUpdateAgent) {
    return <AlertSomethingWentWrong />;
  }

  return isRank &&
    isStatus &&
    isLevel &&
    isBranchOffice &&
    isContractType &&
    isCountry &&
    isSmallBusiness &&
    isSex &&
    !errorGetAgentById ? (
    <Formik
      enableReinitialize
      initialValues={agentResponse?.getAgentv2 || AgentFromLocalStorage() || agentInputWithDefaultValues()}
      onSubmit={(values, { setSubmitting }) => {
        const formatetAgentInputValues: PRMBackendAgentInput = formatAgentinputForQuery(values);
        if (values.agent_id === AgentInit.agent_id) {
          createAgent({ variables: { input: formatetAgentInputValues } }).then((result) => {
            reloadData();
            handleClearStorage();
            navigate(`/agent/${result.data.createAgent.agent_id}`);
          });
        } else {
          delete formatetAgentInputValues.photo;
          updateAgent({
            variables: { agent_id: formatetAgentInputValues.agent_id, input: formatetAgentInputValues },
          }).then((result) => {
            if (result.data.updateAgent) {
              refetch();
              setSubmitting(false);
              reloadData();
            }
          });
        }
      }}
      validationSchema={AgentValid(agents)}
      validateOnMount
    >
      {(props: FormikProps<Agentv2 | AgentInputType>) => {
        const {
          resetForm,
          values,
          errors,
          dirty,
          handleSubmit,
          handleReset,
          isValid,
          isSubmitting,
          validateForm,
          setTouched,
        } = props;

        const isFormWithInitValues = JSON.stringify(defaultAgentValue) === JSON.stringify(values);
        handleFormChange(values);

        const isExistingAgent = !!agentResponse?.getAgentv2;
        const isExistingActive = agentResponse?.getAgentv2?.status_id === AgentStatus.active && !!AgentID;

        const errorCounts = getErrorCountsByPanel(errors as Record<string, unknown>);
        const totalErrors = Object.values(errorCounts).reduce((sum, c) => sum + c, 0);

        const onSave = () => {
          handleSaveWithNavigation(validateForm, setTouched as (t: Record<string, boolean>) => void, handleSubmit);
        };

        const onReset = () => {
          if (!isFormWithInitValues && !agentResponse?.getAgentv2) {
            resetForm({ values: agentInputWithDefaultValues() });
          } else {
            handleReset();
          }
        };

        const panelStyle = (panelId: PanelId) => ({
          visibility: activePanel === panelId ? 'visible' : ('hidden' as const),
          height: activePanel === panelId ? 'auto' : 0,
          overflow: 'hidden' as const,
          position: activePanel === panelId ? ('relative' as const) : ('absolute' as const),
          width: '100%',
        });

        return (
          <Box sx={{ maxWidth: 1400, mx: 'auto', p: { xs: 1.5, sm: 3 } }}>
            {values.contract_type_id === 3 && (
              <Alert variant="filled" severity="info" sx={{ mb: 2 }}>
                Berater mit der Vertragsart &quot;ProHolidays&quot; werden nicht mit der CentralNode synchronisiert.
                Haben keinen Zugang zum Cockpit. Vertragsart &quot;ProHolidays&quot; kann nur an Berater der Firma
                &quot;Mein Urlaubsglück GmbH&quot; vergeben werden.
              </Alert>
            )}

            <AgentHeader
              values={values}
              isExistingAgent={isExistingAgent}
              dirty={dirty}
              isValid={isValid}
              isSubmitting={isSubmitting}
              isFormWithInitValues={isFormWithInitValues}
              errorCount={totalErrors}
              onSave={onSave}
              onReset={onReset}
            />

            <Box
              sx={{
                display: 'flex',
                gap: 2.5,
                flexDirection: isTablet ? 'column' : 'row',
              }}
            >
              <AgentSidebar activePanel={activePanel} onPanelChange={setActivePanel} errorCounts={errorCounts} />

              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  position: 'relative',
                }}
              >
                <Box sx={panelStyle('personal')}>
                  <PersonalPanel />
                </Box>
                <Box sx={panelStyle('contract')}>
                  <ContractPanel />
                </Box>
                <Box sx={panelStyle('finance')}>
                  <FinancePanel />
                </Box>
                <Box sx={panelStyle('systems')}>
                  <SystemsPanel agentId={AgentID} isExistingActive={isExistingActive} />
                </Box>
                <Box sx={panelStyle('costs')}>
                  <CostsPanel />
                </Box>
                <Box sx={panelStyle('systemAccess')}>
                  <SystemAccessPanel agentId={AgentID} />
                </Box>
                <Box sx={panelStyle('docs')}>
                  <DocumentsPanel />
                </Box>
                <Box sx={panelStyle('notes')}>
                  <NotesPanel />
                </Box>
                <Box sx={panelStyle('dismiss')}>
                  <DismissPanel />
                </Box>
              </Box>
            </Box>
          </Box>
        );
      }}
    </Formik>
  ) : (
    <AgentSkeleton />
  );
}
